type GlyphProps = {
  size?: number
  className?: string
}

/** Level / rank badge — gold shield + star, no outer square frame */
export function RankGlyph({ size = 28, className = '' }: GlyphProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      aria-hidden="true"
      style={{ display: 'block', background: 'transparent' }}
    >
      <defs>
        <linearGradient id="rank-gold" x1="0.2" y1="0" x2="0.8" y2="1">
          <stop offset="0%" stopColor="#FFE9A0" />
          <stop offset="45%" stopColor="#F0B429" />
          <stop offset="100%" stopColor="#C07A0C" />
        </linearGradient>
        <linearGradient id="rank-fill" x1="0.5" y1="0" x2="0.5" y2="1">
          <stop offset="0%" stopColor="#8B5CFF" />
          <stop offset="100%" stopColor="#4A28B8" />
        </linearGradient>
      </defs>
      {/* shield */}
      <path
        d="M32 4 L56 14 V34 C56 48 44 58 32 62 C20 58 8 48 8 34 V14 Z"
        fill="url(#rank-gold)"
      />
      <path
        d="M32 10 L50 18 V34 C50 45 41 53 32 56 C23 53 14 45 14 34 V18 Z"
        fill="url(#rank-fill)"
      />
      {/* star */}
      <path
        d="M32 20 L35.2 28.2 L44 28.6 L37.2 34.2 L39.4 42.8 L32 37.8 L24.6 42.8 L26.8 34.2 L20 28.6 L28.8 28.2 Z"
        fill="#FFE9A0"
        stroke="#C07A0C"
        strokeWidth="1"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Invite / friends — people marks only, no circular border */
export function FriendsGlyph({ size = 28, className = '' }: GlyphProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      aria-hidden="true"
      style={{ display: 'block', background: 'transparent' }}
    >
      <defs>
        <linearGradient id="fr-gold" x1="0.3" y1="0" x2="0.7" y2="1">
          <stop offset="0%" stopColor="#FFE08A" />
          <stop offset="100%" stopColor="#E0A020" />
        </linearGradient>
        <linearGradient id="fr-blue" x1="0.3" y1="0" x2="0.7" y2="1">
          <stop offset="0%" stopColor="#7EC8FF" />
          <stop offset="100%" stopColor="#2A6DE8" />
        </linearGradient>
      </defs>
      {/* left person */}
      <circle cx="16" cy="20" r="8" fill="url(#fr-blue)" />
      <path d="M4 48 C4 36 10 32 16 32 C22 32 28 36 28 48 Z" fill="url(#fr-blue)" />
      {/* right person */}
      <circle cx="48" cy="20" r="8" fill="url(#fr-blue)" />
      <path d="M36 48 C36 36 42 32 48 32 C54 32 60 36 60 48 Z" fill="url(#fr-blue)" />
      {/* center (main) person */}
      <circle cx="32" cy="18" r="10" fill="url(#fr-gold)" />
      <path d="M16 52 C16 38 22 34 32 34 C42 34 48 38 48 52 Z" fill="url(#fr-gold)" />
    </svg>
  )
}

/** Guess-number — target + digits only, no plate / border */
export function GuessGlyph({ size = 28, className = '' }: GlyphProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      aria-hidden="true"
      style={{ display: 'block', background: 'transparent' }}
    >
      <defs>
        <linearGradient id="guess-ring" x1="0.2" y1="0" x2="0.85" y2="1">
          <stop offset="0%" stopColor="#FF7A7A" />
          <stop offset="100%" stopColor="#D4243A" />
        </linearGradient>
        <linearGradient id="guess-gold" x1="0.2" y1="0" x2="0.8" y2="1">
          <stop offset="0%" stopColor="#FFE9A0" />
          <stop offset="100%" stopColor="#E0A020" />
        </linearGradient>
      </defs>
      {/* concentric target — no fill behind */}
      <circle cx="28" cy="34" r="22" fill="none" stroke="url(#guess-ring)" strokeWidth="5" />
      <circle cx="28" cy="34" r="14" fill="none" stroke="#EEF4FF" strokeWidth="4" />
      <circle cx="28" cy="34" r="6" fill="url(#guess-ring)" />
      {/* golden arrow tip into bullseye */}
      <path
        d="M40 10 L52 6 L48 18 L42 16 Z"
        fill="url(#guess-gold)"
      />
      <path
        d="M40 12 L30 30"
        stroke="url(#guess-gold)"
        strokeWidth="3.5"
        strokeLinecap="round"
      />
      {/* small “7” cue for 7-digit game */}
      <text
        x="52"
        y="52"
        textAnchor="middle"
        fontFamily="Vazirmatn, Tahoma, sans-serif"
        fontSize="16"
        fontWeight="800"
        fill="#FFE08A"
      >
        7
      </text>
    </svg>
  )
}

/** Morse — key + · · — signal, no outer frame / bg */
export function MorseGlyph({ size = 28, className = '' }: GlyphProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      aria-hidden="true"
      style={{ display: 'block', background: 'transparent' }}
    >
      <defs>
        <linearGradient id="morse-metal" x1="0.2" y1="0" x2="0.8" y2="1">
          <stop offset="0%" stopColor="#C8D8F0" />
          <stop offset="100%" stopColor="#6A849E" />
        </linearGradient>
        <linearGradient id="morse-gold" x1="0.2" y1="0" x2="0.8" y2="1">
          <stop offset="0%" stopColor="#FFE9A0" />
          <stop offset="100%" stopColor="#E0A020" />
        </linearGradient>
        <linearGradient id="morse-teal" x1="0.2" y1="0" x2="0.8" y2="1">
          <stop offset="0%" stopColor="#7EFFD0" />
          <stop offset="100%" stopColor="#1FA97A" />
        </linearGradient>
      </defs>
      {/* telegraph key base */}
      <rect x="10" y="38" width="28" height="10" rx="3" fill="url(#morse-metal)" />
      <rect x="18" y="28" width="6" height="12" rx="2" fill="url(#morse-metal)" />
      {/* lever */}
      <path
        d="M12 30 H34"
        stroke="url(#morse-gold)"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <circle cx="36" cy="30" r="5" fill="url(#morse-gold)" />
      {/* morse · · — */}
      <circle cx="44" cy="48" r="3.5" fill="url(#morse-teal)" />
      <circle cx="52" cy="48" r="3.5" fill="url(#morse-teal)" />
      <rect x="42" y="14" width="14" height="6" rx="3" fill="url(#morse-teal)" />
      <circle cx="49" cy="28" r="3.5" fill="url(#morse-teal)" />
    </svg>
  )
}

/** Support / help desk — headset + chat, no outer frame */
export function SupportGlyph({ size = 28, className = '' }: GlyphProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      aria-hidden="true"
      style={{ display: 'block', background: 'transparent' }}
    >
      <defs>
        <linearGradient id="sup-gold" x1="0.2" y1="0" x2="0.8" y2="1">
          <stop offset="0%" stopColor="#FFE9A0" />
          <stop offset="50%" stopColor="#F0B429" />
          <stop offset="100%" stopColor="#C07A0C" />
        </linearGradient>
        <linearGradient id="sup-blue" x1="0.3" y1="0" x2="0.7" y2="1">
          <stop offset="0%" stopColor="#9AD0FF" />
          <stop offset="100%" stopColor="#2A6DE8" />
        </linearGradient>
      </defs>
      {/* headset band */}
      <path
        d="M14 34 V26 C14 14 22 8 32 8 C42 8 50 14 50 26 V34"
        fill="none"
        stroke="url(#sup-gold)"
        strokeWidth="5"
        strokeLinecap="round"
      />
      {/* ear cups */}
      <rect x="8" y="30" width="12" height="16" rx="5" fill="url(#sup-gold)" />
      <rect x="44" y="30" width="12" height="16" rx="5" fill="url(#sup-gold)" />
      {/* mic boom */}
      <path
        d="M18 44 C18 52 24 56 32 56"
        fill="none"
        stroke="url(#sup-gold)"
        strokeWidth="3.5"
        strokeLinecap="round"
      />
      <circle cx="32" cy="56" r="4" fill="url(#sup-gold)" />
      {/* chat bubble */}
      <path
        d="M28 22 H48 C51 22 53 24 53 27 V35 C53 38 51 40 48 40 H40 L34 46 V40 H28 C25 40 23 38 23 35 V27 C23 24 25 22 28 22 Z"
        fill="url(#sup-blue)"
      />
      <circle cx="33" cy="31" r="2.2" fill="#EEF4FF" />
      <circle cx="38.5" cy="31" r="2.2" fill="#EEF4FF" />
      <circle cx="44" cy="31" r="2.2" fill="#EEF4FF" />
    </svg>
  )
}

/** Trophy for section titles / leaderboard */
export function TrophyGlyph({ size = 28, className = '' }: GlyphProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      aria-hidden="true"
      style={{ display: 'block', background: 'transparent' }}
    >
      <defs>
        <linearGradient id="tr-gold" x1="0.2" y1="0" x2="0.8" y2="1">
          <stop offset="0%" stopColor="#FFE9A0" />
          <stop offset="50%" stopColor="#F0B429" />
          <stop offset="100%" stopColor="#B8740A" />
        </linearGradient>
      </defs>
      <path
        d="M18 10 H46 V22 C46 34 40 42 32 44 C24 42 18 34 18 22 Z"
        fill="url(#tr-gold)"
      />
      <path d="M18 14 H10 C10 26 16 32 22 34" fill="none" stroke="#F0B429" strokeWidth="5" strokeLinecap="round" />
      <path d="M46 14 H54 C54 26 48 32 42 34" fill="none" stroke="#F0B429" strokeWidth="5" strokeLinecap="round" />
      <rect x="28" y="44" width="8" height="8" rx="1" fill="#E0A020" />
      <rect x="20" y="52" width="24" height="6" rx="2" fill="url(#tr-gold)" />
      <path
        d="M32 18 L33.8 22.8 L39 23 L34.8 26.2 L36.2 31.2 L32 28.4 L27.8 31.2 L29.2 26.2 L25 23 L30.2 22.8 Z"
        fill="#FFF6C8"
      />
    </svg>
  )
}
